import { Button } from '@pets-ui/Button/Button'
import { ButtonToggleVisiblePassword } from '@pets-ui/ButtonToggleVisiblePassword/ButtonToggleVisiblePassword'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowLeftSmall } from '@pets-ui/icons'
import { Input } from '@pets-ui/Input/Input'
import { useWpLayout } from '@pets-ui/useWpLayout/useWpLayout'
import { VStack } from '@pets-ui/VStack/VStack'
import { type AxiosError } from 'axios'
import Cookies from 'js-cookie'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'
import { useRequestRestPassword } from '../../../hooks/apis/wp/useRequestRestPassword'
import { InputVerify } from '../../InputVerify'
import { useContextCosign } from '../../useContextCosign'
import { CardLogin } from '../CardLogin'
import { CardTitleWithIcon } from '../login/CardTitleWithIcon'
import { PasswordSuccessType } from '../login/types'
import { useContextMyAccountDialog } from '../myAccount/Provider/useContextMyAccountDialog'
import { Service } from '../Service'
import { validationPassword } from './validationPassword'

export function PasswordChange() {
  const { t } = useTranslation()
  const [pastPassword, setPastPassword] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { showDialogServerError } = useContextMyAccountDialog()
  const [serverError, setServerError] = useState<string>()
  const { passwordErrors, isSame, passwordError, confirmPasswordError } = validationPassword({ password, confirmPassword })
  const { trigger, isMutating } = useRequestRestPassword()
  const history = useHistory()
  const { title } = useContextCosign()
  useWpLayout({ footer: false })

  const handleClickRegisterPassword = async () => {
    try {
      await trigger({ pastPassword, password })
      Cookies.set('password_success', String(PasswordSuccessType.CHANGE))
      history.replace('/password/success')
    }
    catch (e) {
      const error = e as AxiosError
      if (error.response?.status === 422) {
        setServerError(String(t('login.currentPAsswordIncorrect')))
        return
      }
      showDialogServerError()
    }
  }

  const handleClickBack = () => {
    window.location.href = '/my-account/edit-account/'
  }

  const hasEmpty = [pastPassword, password, confirmPassword].some(item => item.trim() === '')
  const canSubmit = isSame && !passwordError && !hasEmpty

  return (
    <CardLogin>
      <Helmet>
        <title>
          {t('login.changePassword')}
          {' '}
          -
          {' '}
          {title}
        </title>
      </Helmet>
      <CardTitleWithIcon
        title={t('login.changePassword')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={handleClickBack}
      />
      <VStack gap={4}>
        <VStack gap={1}>
          <Input
            value={pastPassword}
            onChange={e => setPastPassword(e.target.value)}
            label={t('login.currentPassword')}
            placeholder={String(t('login.pleaseEnter'))}
            fullWidth
            type={showOldPassword ? 'text' : 'password'}
            endAdornment={<ButtonToggleVisiblePassword show={showOldPassword} onClick={() => setShowOldPassword(!showOldPassword)} />}
            error={!!serverError}
            helpText={serverError}
          />
        </VStack>
        <VStack gap={1}>
          <Input
            value={password}
            onChange={e => setPassword(e.target.value)}
            label={t('login.newPassword')}
            placeholder={String(t('login.pleaseEnter'))}
            fullWidth
            type={showPassword ? 'text' : 'password'}
            endAdornment={<ButtonToggleVisiblePassword show={showPassword} onClick={() => setShowPassword(!showPassword)} />}
            error={passwordError}
          />
          <HStack gap={3}>
            <InputVerify success={passwordErrors.characters} rule={t('login.leastEightCharacters')} />
            <InputVerify success={passwordErrors.englishLetter} rule={t('login.oneLetter')} />
            <InputVerify success={passwordErrors.numberLetter} rule={t('logib.oneNumber')} />
          </HStack>
        </VStack>
        <VStack gap={1}>
          <Input
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            label={t('login.confirmNewPassword')}
            placeholder={String(t('login.reEnterNewPassword'))}
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleClickRegisterPassword()
              }
            }}
            endAdornment={<ButtonToggleVisiblePassword show={showConfirmPassword} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />}
            error={confirmPasswordError}
            helpText={confirmPasswordError ? t('login.passwordsNotMatch') : ''}
          />
        </VStack>
        <Button
          onClick={handleClickRegisterPassword}
          disabled={!canSubmit}
          loading={isMutating}
        >
          {t('login.changePassword')}
        </Button>
      </VStack>
      <Service />
    </CardLogin>
  )
}
